/**
 * One Run.
 *
 * While it is alive this screen polls for phase and record count. When it has
 * failed, the human message and the hint are what the screen is about; the
 * diagnostic envelope is available but folded away, so the everyday view stays
 * readable and a developer can still get at what they need.
 */

import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api, type Run } from "../api";
import { POLL_RUN_MS, isSettled, keys } from "../queries";
import { Layout } from "../components/Layout";
import {
  Badge,
  Banner,
  Button,
  Card,
  Code,
  cx,
  KeyValue,
  Muted,
  Spinner,
  StatTile,
  StatusBadge,
} from "../components/ui";
import { DownloadIcon } from "../components/icons";
import { formatDuration, formatTimestamp, humaniseCode } from "../format";

// `authenticating` only appears for a source that has to sign in, and it is
// where a Run spends its time when a session has lapsed and a browser is
// being driven — so it is worth showing rather than leaving the progress bar
// apparently stuck before the first request.
//
// `expanding` is the same argument for the same reason (ADR-0035). A Feed whose
// records each name a further address spends the bulk of its Run there — a
// roster of 700 players is 700 sequential requests — and a phase missing from
// this list is worse than one merely unlabelled: `indexOf` returns -1, so no
// step reads as current and none reads as done, and the whole track goes dark
// for the longest stage of the Run. Which is what a hung Run looks like.
const PHASES = [
  "authenticating",
  "fetching",
  "parsing",
  "expanding",
  "ranking",
  "uploading",
] as const;

export function RunDetail() {
  const { runId = "" } = useParams();
  const queryClient = useQueryClient();

  const { data: run, error: loadError } = useQuery({
    queryKey: keys.run(runId),
    queryFn: () => api.readRun(runId),
    // Polling stops the moment the Run reaches a state it can never leave —
    // returning `false` is what tells the query to stand down, so a finished
    // Run does not keep a timer alive for as long as the tab is open.
    refetchInterval: (query) => (isSettled(query.state.data?.status) ? false : POLL_RUN_MS),
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelRun(runId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.run(runId) });
      // The Run's own list view shows this status too.
      await queryClient.invalidateQueries({ queryKey: keys.profiles });
    },
  });

  const error = loadError
    ? loadError instanceof ApiError
      ? loadError.message
      : "Could not load this Run."
    : cancel.error
      ? "Could not cancel this Run."
      : null;

  if (!run) {
    return (
      <Layout breadcrumbs={[{ label: "Sources", to: "/sources" }]}>
        {error ? <Banner tone="danger" title={error} /> : <Spinner label="Loading Run" />}
      </Layout>
    );
  }

  const live = run.status === "running" || run.status === "queued";

  return (
    <Layout
      // A Run belongs to a Source Profile or to a Transfer Portal Profile, and
      // the two live on different screens. Reading `source_profile_id`
      // unconditionally sent a Transfer Portal Run's trail to `/sources/null`,
      // which is where the Run button on that tab lands you.
      breadcrumbs={
        run.transfer_portal_profile_id
          ? [{ label: "Transfer Portal", to: "/transfer-portal" }, { label: "Run" }]
          : [
              { label: "Sources", to: "/sources" },
              { label: "Source", to: `/sources/${run.source_profile_id}` },
              { label: "Run" },
            ]
      }
      title="Run"
      actions={
        live ? (
          <Button variant="danger" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
            {cancel.isPending ? "Cancelling…" : "Cancel this Run"}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-lg">
        <div className="flex flex-wrap items-center gap-xs">
          <StatusBadge status={run.status} />
          <Badge>{run.trigger === "test" ? "Test run" : "Manual run"}</Badge>
          {run.trigger === "test" && <Badge tone="warning">nothing uploaded</Badge>}
        </div>

        {run.failure_code && (
          <Banner tone="danger" title={run.failure_message ?? humaniseCode(run.failure_code)}>
            {run.failure_hint}
          </Banner>
        )}

        {run.status === "skipped" && (
          <Banner tone="info" title="Nothing to do">
            Every Feed answered that it is unchanged since the last Run, so the portal downloaded
            nothing and wrote no file. This is not an error.
          </Banner>
        )}

        {live && <PhaseTrack run={run} />}

        <div className="grid grid-cols-1 gap-md fold:grid-cols-3">
          <StatTile
            label="Records processed"
            value={run.records_processed.toLocaleString()}
            hint="Read from the feed"
          />
          <StatTile
            label="Players"
            value={run.player_count?.toLocaleString() ?? "—"}
            hint="After the participation rule"
          />
          <StatTile label="Duration" value={formatDuration(run.duration_ms)} hint="Start to finish" />
        </div>

        <div className="grid grid-cols-1 gap-lg fold:grid-cols-2">
          <Card title="What happened">
            <div className="flex flex-col gap-md">
              <div className="grid grid-cols-1 gap-md fold:grid-cols-2">
                <KeyValue label="Started by">{run.started_by ?? <Muted>—</Muted>}</KeyValue>
                <KeyValue label="Requests">
                  {run.requests_made} made
                  {run.requests_skipped > 0 && <> · {run.requests_skipped} skipped as unchanged</>}
                </KeyValue>
                <KeyValue label="Started">{formatTimestamp(run.started_at)}</KeyValue>
                <KeyValue label="Finished">{formatTimestamp(run.finished_at)}</KeyValue>
              </div>
              {run.ranking_method && (
                <KeyValue label="Ranking method">
                  {run.ranking_method} v{run.ranking_version}
                </KeyValue>
              )}
            </div>
          </Card>

          <Card title="Output">
            {run.available_downloads.length > 0 ? (
              <div className="flex flex-col gap-sm">
                {run.trigger === "test" && (
                  <Muted>
                    This was a test Run. The portal produced the files but uploaded nothing.
                  </Muted>
                )}
                <div className="flex flex-col gap-sm">
                  {run.available_downloads.map((filename) => {
                    const keys = run.output_keys[filename];
                    return (
                      <div key={filename} className="flex flex-col gap-xxs">
                        <a
                          href={api.downloadUrl(run.id, filename)}
                          download
                          className="flex items-center gap-xs rounded-md border border-hairline px-sm py-xs text-body-sm-medium text-ink no-underline hover:no-underline"
                        >
                          <DownloadIcon size={16} />
                          {filename}
                        </a>
                        {keys && <Code className="block">{keys.stable}</Code>}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <Muted>The portal wrote no files.</Muted>
            )}

            {run.cohort_fallbacks.length > 0 && (
              <div className="mt-md">
                <Banner
                  tone="info"
                  title={`${run.cohort_fallbacks.length} statistic${
                    run.cohort_fallbacks.length === 1 ? "" : "s"
                  } fell back`}
                >
                  Percentiled against the whole Run because their position group was too small to be
                  meaningful.
                </Banner>
              </div>
            )}
          </Card>
        </div>

        {run.diagnostics && (
          <details className="envelope">
            <summary>Technical detail</summary>
            <div className="envelope-body">
              <Muted>
                Stored credentials are redacted from this envelope, which ages out at the end of
                the retention window.
              </Muted>
              <pre>{JSON.stringify(run.diagnostics, null, 2)}</pre>
            </div>
          </details>
        )}
      </div>
    </Layout>
  );
}

/**
 * The one dark panel outside the status badge. A Run in flight is the single
 * moment this screen is worth watching, and the inversion says so without a
 * spinner in the middle of the page.
 */
function PhaseTrack({ run }: { run: Run }) {
  const reachedIndex = PHASES.indexOf(run.phase as (typeof PHASES)[number]);

  return (
    <div className="rounded-lg bg-surface-dark p-lg text-on-dark">
      <div className="mb-md text-overline uppercase text-on-dark-soft">
        In progress
      </div>

      <div className="mb-md flex flex-wrap gap-xs">
        {PHASES.map((phase, index) => {
          const current = run.phase === phase;
          const done = reachedIndex > index;
          return (
            <span
              key={phase}
              className={cx(
                "min-w-[96px] flex-auto rounded-pill border px-sm py-xs text-center text-caption",
                current
                  ? "animate-pulse-soft border-on-dark bg-on-dark text-ink"
                  : cx(
                      "border-hairline-dark bg-surface-dark-elevated",
                      done ? "text-on-dark-soft" : "text-muted-soft",
                    ),
              )}
            >
              {phase}
            </span>
          );
        })}
      </div>

      {/* Which Feed, while fetching; records once there are any.
          The record count stays at zero for the whole fetch, so on a source
          with thirteen 6.9 MB reports it is the wrong number to show: several
          minutes of a motionless zero is what a hung Run looks like too. A Run
          recorded before this counter existed reports no Feeds at all, and
          falls through to the record count as it always did. */}
      {run.expansions_total > 0 ? (
        <>
          {/* Once a Feed is expanding, this is the only number that moves.
              Ahead of the Feed count because by the time the first expansion
              is issued every Feed is already retrieved — so the Feed count is
              sitting complete on screen while nearly all of the work is still
              to come, which is ADR-0035's first Consequence exactly. */}
          <div className="font-display text-display-sm">
            {run.expansions_done.toLocaleString()} of{" "}
            {run.expansions_total.toLocaleString()}
          </div>
          <div className="text-body-sm text-on-dark-soft">
            records expanded so far
            {run.expansions_failed > 0
              ? ` · ${run.expansions_failed.toLocaleString()} failed`
              : ""}
          </div>
        </>
      ) : run.feeds_total > 1 && run.records_processed === 0 ? (
        <>
          <div className="font-display text-display-sm">
            {run.feeds_fetched} of {run.feeds_total}
          </div>
          <div className="text-body-sm text-on-dark-soft">feeds retrieved so far</div>
        </>
      ) : (
        <>
          <div className="font-display text-display-sm">
            {run.records_processed.toLocaleString()}
          </div>
          <div className="text-body-sm text-on-dark-soft">records processed so far</div>
        </>
      )}
    </div>
  );
}
